import React, { useEffect, useState } from "react";

import IncrementQuantityPicker from "../shared/IncrementQuantityPicker";
import Section from "../sections/Section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isValuePresent, isProductHeroBuiltinContentVisible } from "../../utils/builderUtils";
import { resolveStaticDefaultQuantity as resolveStaticDefaultQuantityValue } from "../../utils/quantityDefault";
import ProductVariantPickerService from "../../packs/services/convertflow/elements/product_variant_picker_service";
import { buildProductHeroStylesheet } from "./ElementProductHeroStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const ElementProductHeroImage = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element } = props;

  const imagePlaceholderClass = ["ShopifyGraphql"].includes(element.options["products-source"])
    ? "cf-shopify-product-placeholder-svg"
    : "cf-generic-product-placeholder-svg"

  return (
    <React.Fragment>
      <div className="cf-prod-hero-inner-box">
        <div className="cf-prod-hero-image-box">
          <div className="cf-product-item-image-block" key={`product-hero-image-${product.id}`}>
            <div className="cf-products-item-image-wrapper">
              <div className="cf-row cf-outer-center cf-products-item-image">
                {product?.image ? <img loading="lazy" src={product.image} /> : <img className={`cf-products-placeholder-svg ${imagePlaceholderClass}`} /> }
              </div>
            </div>

            {element.options["products-images-to-show"] == "carousel" &&
              element.options["products-source"] == "ShopifyGraphql" && (
                <div className="cf-product__slider-wrap">
                  <div className="cf-product__slider">
                    {product?.imageList?.length > 0 && (
                      Object.values(product.imageList).map((image, index) => {
                        return (
                          <img
                            key={`product-hero-image-${product.id}-${image.transformedSrc}`}
                            className={`cf-product__image cf-product__image--${
                              index == 0 ? "active" : ""
                            }`}
                            src={image.transformedSrc}
                          />
                        );
                      })
                    )}

                    {!product?.imageList && (
                      <React.Fragment>
                        <img key={`product-hero-${product.id}-placeholder-1`} className={`cf-product__image cf-product__image--active ${imagePlaceholderClass}`} style={{ backgroundColor: 'rgba(105, 114, 123, 0.5)' }} />
                        <img key={`product-hero-${product.id}-placeholder-2`} className={`cf-product__image ${imagePlaceholderClass}`} style={{ backgroundColor: 'rgba(105, 114, 123, 0.5)' }} />
                        <img key={`product-hero-${product.id}-placeholder-3`} className={`cf-product__image ${imagePlaceholderClass}`} style={{ backgroundColor: 'rgba(105, 114, 123, 0.5)' }} />
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
};

const ElementProductHeroTitle = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element, productTitleText } = props;

  return (
    <React.Fragment>
      <div
        key={`product-hero-title-${product.id}`}
        className={`cf-products-item-title cf-merge-tag-allowed`}
        data-font={element.options["products-item-title-font"]}
      >
        <p dangerouslySetInnerHTML={{ __html: element.options["products-title-override-text"] ? element.options["products-title-override-text"] : productTitleText }} />
      </div>
    </React.Fragment>
  )
}

const ElementProductHeroOfferBadge = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element, website, selectedProductVariant } = props;

  let showCurrencyCode = false;

  let productDiscount;
  if (
    element.options["products-discount-type"] == "amount" &&
    element.options["products-discount-amount"]
  ) {
    showCurrencyCode = true;
    productDiscount = element.options["products-discount-amount"];
  } else if (
    element.options["products-discount-type"] == "percentage" &&
    element.options["products-discount-percentage"]
  ) {
    productDiscount = `${element.options["products-discount-percentage"]}%`;
  } else {
    productDiscount = "10%";
  }

  let productImageBadgeText;
  if (element.options["products-show-image-sold-out-badge"] == "true" && product.available === false) {
    productImageBadgeText = element.options["products-image-sold-out-badge-text"] || "SOLD OUT";
  } else if (element.options["products-image-badge-style"] == "custom") {
    productImageBadgeText = element.options["products-image-badge-custom-message"] || "10% OFF"
  } else if (element.options["products-discount-type"] == "detect") {
    const productAdapter = selectedProductVariant || product;

    if (productAdapter?.compare_at_price) {
      showCurrencyCode = true;
      productImageBadgeText = `${((productAdapter?.compare_at_price / 100) - productAdapter?.price).toFixed(2)} OFF`
    }
  } else {
    productImageBadgeText = productDiscount + " OFF";
  }

  return (
    <React.Fragment>
      <div className="cf-products-item-badge" data-font={element.options["products-image-badge-font-family"]}>
        {showCurrencyCode && <span className="cf-products-currency-code">{website.currency_character}</span>}

        {productImageBadgeText}
      </div>
    </React.Fragment>
  );
};

const ElementProductHeroReviews = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element } = props;

  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (element.options["products-reviews-source"] && product?.id) {
      const integration = Object.values(props.website.integrations).find(
        (i) =>
          i.integration_type == element.options["products-reviews-source"] &&
          i.status == true
      );

      if (integration) {
        new ReviewsIntegrationsService(integration.integration_type, integration)
          .getReviews([product.id])
          .then((response) => {
            setReviews(response[0])
          });
      } else {
        setReviews(null);
      }
    }
  }, [product?.id, element.options["products-reviews-source"]]);

  const renderStarIcon = (score) => {
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 !== 0;

    const starIcons = [];

    for (let i = 0; i < fullStars; i++) {
      starIcons.push(<FontAwesomeIcon key={`product-hero-reviews-star-${i}`} icon="fa-star" />);
    }

    if (hasHalfStar) {
      starIcons.push(<FontAwesomeIcon key={`product-hero-reviews-star-half-${i}`} icon="fa-star-half-stroke" />);
    }

    const remainingStars = 5 - starIcons.length;
    for (let i = 0; i < remainingStars; i++) {
      starIcons.push(<FontAwesomeIcon key={`product-hero-reviews-star-empty-${i}`} icon="fa-regular fa-star" color="white" />);
    }

    return starIcons;
  };

  return (
    <React.Fragment>
      <div key={`product-hero-reviews-${product.id}`} className="cf-products-reviews-wrapper">
        <div className="cf-products-reviews" data-font={element.options["products-reviews-font-family"]}>
          <div className="cf-products-reviews-stars-inner">
            {reviews ? (
              renderStarIcon(reviews.rating)
            ) :
              <React.Fragment>
                <FontAwesomeIcon icon="fa-star" />
                <FontAwesomeIcon icon="fa-star" />
                <FontAwesomeIcon icon="fa-star" />
                <FontAwesomeIcon icon="fa-star" />
                <FontAwesomeIcon icon="fa-star" />
              </React.Fragment>
            }
          </div>

          { reviews && reviews.product_total_reviews > 0 &&
            <div className="cf-products-reviews-count"> {reviews.product_total_reviews}</div>}

          { reviews && reviews.product_total_reviews > 0 &&
            <div className="cf-products-reviews-label">{element.options["products-reviews-label"] ? element.options["products-reviews-label"] : "Reviews" }</div>}
        </div>
      </div>
    </React.Fragment>
  );
};

const ElementProductHeroPrice = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element, website, selectedProductVariant } = props;

  function addZeroes(num) {
    const dec = (num + "").split(".")[1];
    const len = dec && dec.length > 2 ? dec.length : 2;
    return Number(num).toFixed(len);
  }

  const productAdapter = selectedProductVariant || product;

  let priceFormatted = "0.00";
  if (productAdapter?.price) {
    priceFormatted = addZeroes(productAdapter?.price);
    if ((priceFormatted + "").split(".")[1] == "00") {
      priceFormatted = Math.trunc(priceFormatted);
    }
  }

  let discountedPriceFormatted;

  if (element.options["products-discount-type"] == "amount") {
    const discountAmount = element.options["products-discount-amount"] || 10;

    discountedPriceFormatted = (productAdapter?.price - discountAmount).toFixed(2);
  }

  if (element.options["products-discount-type"] == "percentage") {
    const discountAmount = element.options["products-discount-percentage"] || 10;

    discountedPriceFormatted = (
      productAdapter?.price -
      productAdapter?.price * (parseInt(discountAmount) * 0.01)
    ).toFixed(2);
  }

  if (element.options["products-discount-type"] == "detect") {
    if (productAdapter?.compare_at_price) {
      priceFormatted = productAdapter.compare_at_price / 100;
      discountedPriceFormatted = productAdapter.price;
    }
  }

  if (discountedPriceFormatted && discountedPriceFormatted <= 0) {
    discountedPriceFormatted = "0.00";
  }

  if (discountedPriceFormatted) {
    discountedPriceFormatted = addZeroes(discountedPriceFormatted);

    if ((discountedPriceFormatted + "").split(".")[1] == "00") {
      discountedPriceFormatted = Math.trunc(discountedPriceFormatted);
    }

    if (discountedPriceFormatted == 0) {
      discountedPriceFormatted = "0";
    }
  }

  return (
    <React.Fragment>
      <div
        key={`product-hero-price-${product.id}`}
        data-font={element.options["products-item-price-font"]}
        className={`cf-products-item-price`}
        data-discount={discountedPriceFormatted ? true : undefined}
      >
        <p>
          {!discountedPriceFormatted && (
            <React.Fragment>
              {" "}
              <span className="cf-products-currency-code">{website.currency_character}</span>
              <span className="cf-products-item-price-base">
                {parseFloat(priceFormatted).toFixed(2)}
              </span>
            </React.Fragment>
          )}
          {discountedPriceFormatted && (
            <React.Fragment>
              <strike>
              <span className="cf-products-currency-code">{website.currency_character}</span>
                <span className="cf-products-item-price-base">
                  {parseFloat(priceFormatted).toFixed(2)}
                </span>
              </strike>{" "}
              <span className="cf-products-currency-code">{website.currency_character}</span>
              <span className="cf-products-item-price-discount">
                {parseFloat(discountedPriceFormatted).toFixed(2)}
              </span>
            </React.Fragment>
          )}
        </p>
      </div>
    </React.Fragment>
  );
};

const ElementProductHeroDescription = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element, productTitleText } = props;

  let productDescriptionContent = element.options["products-description-override-text"]
    ? element.options["products-description-override-text"]
    : product?.description
    ? product.description
    : `<p>Example Description For ${productTitleText}</p>`;

  if (props.builder == true && element.options["products-description-formatting"] == "cf-unformatted-text") {
    let tempDiv = document.createElement("div");
    tempDiv.innerHTML = productDescriptionContent;
    productDescriptionContent = tempDiv.textContent || tempDiv.innerText || "";
  }

  return (
    <React.Fragment>
      <div
        key={`product-hero-description-${product.id}`}
        className={`cf-products-item-description cf-merge-tag-allowed`}
        data-font={element.options["products-item-description-font"]}
        dangerouslySetInnerHTML={{ __html: productDescriptionContent }}
      />
    </React.Fragment>
  );
};

const ElementProductHeroQuantityPicker = (props) => {
  if (props.condition == false || props.element.options["products-source"] !== "ShopifyGraphql") {
    return null;
  }

  const { element, product } = props;

  const resolveStaticDefaultQuantity = () => {
    return resolveStaticDefaultQuantityValue(element.options);
  };

  return (
    <React.Fragment>
      <div key={`product-hero-quantity-picker-${product.id}`} style={{ display: element.options["confirmation-type"] == "cart" ? 'block' : 'none'}}>
        <IncrementQuantityPicker label={element.options["products-quantity-label-text"]} value={resolveStaticDefaultQuantity()}/>
      </div>
    </React.Fragment>
  )
}

const ElementProductHeroVariantPicker = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { product, element, productTitleText } = props;

  const variantPickerLabel = product?.options ? product?.options.map((f) => f.name == "Title" ? "Options" : f.name).join(" / ") : "Option";

  return (
    <React.Fragment>
      <div className="cf-variant-picker-wrapper" key={`product-hero-variant-picker-${product.id}`} style={{ display: ProductVariantPickerService.shouldShow(element, element.options["products-source"]) ? 'block' : 'none', width: '100%' }}>
        {element.options["products-variant-picker-style"] == "cf-options-buttons" &&
          element.options["products-source"] == "ShopifyGraphql" ? (
            <React.Fragment>
              {product?.options && product?.options.map((option) => {
                let filteredOptions = option.values.filter(e => e !== 'Default Title')

                return (
                  <React.Fragment key={`product-hero-variant-picker-${product.id}-${option.name}`}>
                    {option.name !== "Title" && (
                      <p className="cf-variant-picker-label" style={{ fontSize: "13px" }}>
                        {option.name}
                      </p>
                    )}

                    {filteredOptions.length > 0 && (
                      <div className="cf-products-options-wrapper" data-font={element.options["products-variant-picker-font-family"]}>
                        {filteredOptions.map((value, i) => {
                          return <div key={`${value}-${i}`} className={`cf-products-option-item ${i == 0 ? 'cf-products-item-button--selected' : ''}`}>{value}</div>;
                        })}
                      </div>
                    )}

                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ) : (
            <div className="cf-form-2 cf-form-theme-default cf-field-regular cf-products-item-variants">
              <p className="cf-variant-picker-label" style={{ fontSize: "13px" }}>
                {variantPickerLabel}
              </p>

              <select disabled={props.builder == true ? true : undefined}>
                {product && product?.variants && product?.variants.length > 0 ? (
                  <React.Fragment>
                    {product?.variants.map((productVariant) => {
                      return (
                        <option key={`product-hero-variant-picker-${product.id}-${productVariant.id}`} value={productVariant.id} dangerouslySetInnerHTML={{ __html: productVariant.title == "Default Title" ? product?.title : productVariant.title || productTitleText }} />
                      );
                    })}
                  </React.Fragment>
                ) : (
                  <option value="">{product?.title}</option>
                )}
              </select>
            </div>
          )}
      </div>
    </React.Fragment>
  )
}

const ElementProductHeroSubscription = (props) => {
  if (props.condition == false || props.element.options["products-source"] !== "ShopifyGraphql") {
    return null;
  }

  const { product, element, website } = props;
  const subscriptionChoicesType = element.options["products-subscription-choices-type"] || "dropdown";
  const isSubscriptionDropdownType = subscriptionChoicesType == "dropdown";
  const isSubscriptionRadioType = subscriptionChoicesType == "radios";
  const isSubscriptionCardType = subscriptionChoicesType == "card";

  const preselectedPlanIndex = element.options["products-subscription-preselected-plan-index"] || "";
  const normalizedPreselectedPlanIndex = preselectedPlanIndex != null ? String(preselectedPlanIndex) : "";
  const hasPreselectedPlan = normalizedPreselectedPlanIndex !== "";
  const isDefaultPlanPreselected = normalizedPreselectedPlanIndex === "default";

  // Use variant-level subscriptions for the preview so it matches live output.
  // If the product is already a variant (specific variant selected), its subscriptions
  // are already variant-level. Otherwise fall back to the first variant's subscriptions.
  const displaySubscriptions = product?.is_variant
    ? (product?.subscriptions || [])
    : (product?.variants?.[0]?.subscriptions || product?.subscriptions || []);

  // Build a plan-id → group-name lookup from the product-level cache so that
  // "Show Group Name" works correctly even when displaying variant-level subscriptions
  // (where subscriptionGroup.name === subscription.name and the real group name is lost).
  const planGroupNameMap = {};
  const productSubsCache = typeof window !== 'undefined' ? window.cfBuilderProductSubscriptions?.[element.id] : null;
  if (productSubsCache?.subscriptions) {
    productSubsCache.subscriptions.forEach((group) => {
      Object.values(group.sellingPlans).forEach((plan) => {
        planGroupNameMap[plan.id] = group.name;
      });
    });
  }

  const getSubscriptionLabel = (subscriptionGroup, subscription) => {
    if (element.options["products-subscription-show-group-name"] === "false") {
      return subscription.name;
    }
    const groupName = planGroupNameMap[subscription.id] || subscriptionGroup.name;
    if (groupName === subscription.name) return subscription.name;
    return `${groupName} - ${subscription.name}`;
  };

  const handleCardSelect = (event) => {
    const card = event.currentTarget;
    const wrapper = card.closest(".cf-products-subscription-radio-wrapper");
    const radio = card.querySelector('input[type="radio"]');

    if (!radio || !wrapper) return;

    wrapper.querySelectorAll(".cf-subscription-radio-card").forEach((item) => {
      item.classList.remove("cf-subscription-selected");
    });

    radio.checked = true;
    card.classList.add("cf-subscription-selected");
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const preselectSourceSubscriptions = productSubsCache?.subscriptions?.length
    ? productSubsCache.subscriptions
    : displaySubscriptions;

  // Resolve the actual selling plan id from the settings index using product-level subscriptions.
  let preselectedPlanValue = "";
  if (hasPreselectedPlan && !isDefaultPlanPreselected && preselectSourceSubscriptions.length > 0) {
    let counter = 0;
    outer: for (const group of preselectSourceSubscriptions) {
      for (const plan of Object.values(group.sellingPlans)) {
        if (String(counter) === normalizedPreselectedPlanIndex) {
          preselectedPlanValue = plan.id;
          break outer;
        }
        counter++;
      }
    }
  }
  const preselectedPlanAvailableInDisplay =
    preselectedPlanValue &&
    displaySubscriptions.some((group) =>
      Object.values(group.sellingPlans).some((plan) => plan.id === preselectedPlanValue)
    );
  const stablePlanIndexById = {};
  let stablePlanCounter = 0;
  preselectSourceSubscriptions.forEach((group) => {
    Object.values(group.sellingPlans).forEach((plan) => {
      if (stablePlanIndexById[plan.id] === undefined) {
        stablePlanIndexById[plan.id] = stablePlanCounter;
        stablePlanCounter++;
      }
    });
  });

  const parseNumericAmount = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const selectedVariantForPricing = product?.is_variant ? product : (product?.variants?.[0] || null);
  const selectedVariantCompareAtPrice = selectedVariantForPricing?.compare_at_price
    ? parseNumericAmount(selectedVariantForPricing.compare_at_price / 100)
    : null;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.max(0, amount));
  };

  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return website?.currency_character || "";

    try {
      const parts = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "symbol",
      }).formatToParts(1);
      return parts.find((part) => part.type === "currency")?.value || website?.currency_character || "";
    } catch (error) {
      return website?.currency_character || "";
    }
  };

  const shouldShowPlanPrice = (planIndex) => {
    const planToggle = element.options[`products-subscription-show-price-${planIndex}`];
    if (planToggle === "true") return true;
    if (planToggle === "false") return false;
    return element.options["products-subscription-show-price"] === "true";
  };

  const renderPlanPriceColumn = (planIndex, planPrice, priceCurrency, sellingPlanCompareAtPrice = null) => {
    if (isSubscriptionCardType !== true || !shouldShowPlanPrice(planIndex)) {
      return null;
    }

    const parsedPlanPrice = parseNumericAmount(planPrice);
    if (parsedPlanPrice === null) return null;

    const rawPlanPriceType = element.options["products-subscription-price-type"] || "";
    const planPriceType = rawPlanPriceType === "none" ? "" : rawPlanPriceType;
    const discountAmount = parseNumericAmount(
      element.options["products-subscription-discount-amount"]
    );
    const discountPercentage = parseNumericAmount(
      element.options["products-subscription-discount-percentage"]
    );
    const comparePriceSetting = element.options[`products-subscription-compare-price-${planIndex}`];
    const parsedComparePriceSetting = parseNumericAmount(comparePriceSetting);
    const parsedSellingPlanComparePrice = parseNumericAmount(sellingPlanCompareAtPrice);
    const priceDescription = element.options[`products-subscription-price-description-${planIndex}`];
    const overridePrice = element.options[`products-subscription-override-price-${planIndex}`];
    let currentPrice = parsedPlanPrice;
    let comparePrice = null;

    if (planPriceType === "amount" && discountAmount !== null) {
      comparePrice = parsedPlanPrice;
      currentPrice = Math.max(0, parsedPlanPrice - discountAmount);
    } else if (planPriceType === "percentage" && discountPercentage !== null) {
      comparePrice = parsedPlanPrice;
      currentPrice = Math.max(0, parsedPlanPrice - parsedPlanPrice * (discountPercentage * 0.01));
    } else if (planPriceType === "detect") {
      comparePrice = parsedComparePriceSetting ?? parsedSellingPlanComparePrice ?? selectedVariantCompareAtPrice;
    } else {
      comparePrice = parsedComparePriceSetting;
    }

    const currencySymbol = getCurrencySymbol(priceCurrency);

    return (
      <div
        className="cf-subscription-plan-price-column"
        style={{
          marginLeft: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          alignSelf: "flex-start",
          textAlign: "right",
          lineHeight: "1.2",
          gap: "2px",
        }}
      >
        {(comparePrice !== null || comparePriceSetting) && (
          <div
            className="cf-subscription-plan-compare-price"
            style={{ textDecoration: "line-through", opacity: "0.75", fontSize: "0.9em" }}
          >
            {comparePrice !== null ? `${currencySymbol}${formatAmount(comparePrice)}` : String(comparePriceSetting)}
          </div>
        )}

        <div className="cf-subscription-plan-price" style={{ whiteSpace: "nowrap" }}>
          {overridePrice ? (
            <span className="cf-subscription-plan-price-value">{overridePrice}</span>
          ) : (
            <React.Fragment>
              <span className="cf-subscription-plan-price-currency">{currencySymbol}</span>
              <span className="cf-subscription-plan-price-value">{formatAmount(currentPrice)}</span>
            </React.Fragment>
          )}
        </div>

        {priceDescription && (
          <div className="cf-subscription-plan-price-description" style={{ fontSize: "0.85em", opacity: "0.85" }}>
            {priceDescription}
          </div>
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div key={`product-hero-subscription-${product.id}`} className="cf-products-subscription-dropdown-wrapper" style={{ display: element.options["confirmation-type"] == "cart" ? 'block' : 'none', width: '100%'}}>
        <div className="cf-form-2 cf-form-theme-default cf-field-regular">
          <p className="cf-products-subscription-label" style={{ fontSize: "13px" }}>
            {element.options["products-subscription-label-text"] ? element.options["products-subscription-label-text"] : "Frequency"}
          </p>

          {(isSubscriptionDropdownType && product) && (
            <select
              key={`subs-dropdown-${product?.id}-${preselectedPlanValue}`}
              className="cf-subscription-select"
              defaultValue={
                hasPreselectedPlan
                  ? (preselectedPlanAvailableInDisplay ? preselectedPlanValue : "__cf-no-selection__")
                  : undefined
              }
              disabled={props.builder == true ? true : undefined}
            >
              {(product.requires_selling_plan !== true || displaySubscriptions.length == 0) && (<option value="">{element.options["products-subscription-default-plan-text"] ? element.options["products-subscription-default-plan-text"] : "One-time purchase"}</option>)}

              {displaySubscriptions.map((subscriptionGroup) => {
                return Object.values(subscriptionGroup.sellingPlans).map((subscription) => {
                  return (
                    <option key={`product-hero-subscription-${product.id}-${subscription.id}`} value={subscription.id}>
                      {getSubscriptionLabel(subscriptionGroup, subscription)}
                    </option>
                  );
                })
              })}
            </select>
          )}

          {(isSubscriptionRadioType && product) && (
            <div key={`subs-radios-${product?.id}-${preselectedPlanIndex}`} className="cf-products-subscription-radio-wrapper" data-font={element.options["products-subscription-choices-font-family"]}>
              {(product.requires_selling_plan !== true || displaySubscriptions.length == 0) && (
                <p>
                  <input
                    type="radio"
                    id="one-time-purchase"
                    name="cf-subscription-type"
                    value=""
                    defaultChecked={hasPreselectedPlan && isDefaultPlanPreselected}
                  />
                  <label htmlFor="one-time-purchase">
                    {element.options["products-subscription-default-plan-text"] || "One-time purchase"}
                  </label>
                </p>
              )}

              {(() => {
                let radioCounter = 0;
                const radios = [];
                displaySubscriptions.forEach((subscriptionGroup) => {
                  Object.values(subscriptionGroup.sellingPlans).forEach((subscription) => {
                    const currentIndex = radioCounter;
                    const hasDefault = product.requires_selling_plan !== true || displaySubscriptions.length == 0;
                    const isChecked = hasPreselectedPlan
                      ? (preselectedPlanAvailableInDisplay && subscription.id === preselectedPlanValue)
                      : false;
                    radios.push(
                      <p key={`product-hero-subscription-${product.id}-${subscription.id}`}>
                        <input type="radio" id={subscription.id} name="cf-subscription-type" value={subscription.id} defaultChecked={isChecked} />
                        <label htmlFor={subscription.id}>{getSubscriptionLabel(subscriptionGroup, subscription)}</label>
                      </p>
                    );
                    radioCounter++;
                  });
                });
                return radios;
              })()}
            </div>
          )}

          {(isSubscriptionCardType && product) && (
            <div key={`subs-cards-${product?.id}-${preselectedPlanIndex}`} className="cf-products-subscription-radio-wrapper" data-font={element.options["products-subscription-choices-font-family"]}>
              {(() => {
                let planCounter = 0;
                const hasOneTime = product.requires_selling_plan !== true || displaySubscriptions.length == 0;

                const cards = [];

                if (hasOneTime && element.options["products-subscription-hide-default"] !== "false") {
                  const showBadge = element.options["products-subscription-popular-badge-default"] == "true";
                  const badgeText = element.options["products-subscription-badge-text-default"] || "MOST POPULAR";
                  const description = element.options["products-subscription-description-default"] || null;
                  const defaultPlanPrice = product?.is_variant ? product?.price : (product?.variants?.[0]?.price ?? product?.price);
                  const defaultPlanCurrency = product?.is_variant ? product?.price_currency : (product?.variants?.[0]?.price_currency || product?.price_currency);
                  const isDefaultChecked = hasPreselectedPlan && isDefaultPlanPreselected;
                  cards.push(
                    <div
                      className={`cf-subscription-radio-card${isDefaultChecked ? ' cf-subscription-selected' : ''}`}
                      data-plan-index="default"
                      key="subscription-card-default"
                      onClick={handleCardSelect}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleCardSelect(event);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      {showBadge && <div className="cf-subscription-popular-badge">{badgeText}</div>}
                      <div className="cf-subscription-radio-card-inner">
                        <div className="cf-subscription-radio-row" style={{ margin: 0, display: "flex", alignItems: "flex-start", gap: "10px", paddingTop: "2px" }}>
                          <input type="radio" id="one-time-purchase" name="cf-subscription-type" value="" defaultChecked={isDefaultChecked} />
                          <div className="cf-subscription-plan-details" style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                            <label htmlFor="one-time-purchase">
                              {element.options["products-subscription-default-plan-text"] || "One-time purchase"}
                            </label>
                            {description && <div className="cf-subscription-radio-description" dangerouslySetInnerHTML={{ __html: description.replace(/text-align:\s*([^;"]+)/g, 'text-align: $1 !important') }} />}
                          </div>
                          {renderPlanPriceColumn("default", defaultPlanPrice, defaultPlanCurrency)}
                        </div>
                      </div>
                    </div>
                  );
                }

                displaySubscriptions.forEach((subscriptionGroup) => {
                  Object.values(subscriptionGroup.sellingPlans).forEach((subscription) => {
                    const currentIndex = planCounter;
                    const settingsIndex = stablePlanIndexById[subscription.id] ?? currentIndex;
                    if (element.options[`products-subscription-hide-${settingsIndex}`] !== "false") {
                      const showBadge = element.options[`products-subscription-popular-badge-${settingsIndex}`] == "true";
                      const badgeText = element.options[`products-subscription-badge-text-${settingsIndex}`] || "MOST POPULAR";
                      const description = element.options[`products-subscription-description-${settingsIndex}`] || null;
                      const isChecked = hasPreselectedPlan
                        ? (preselectedPlanAvailableInDisplay && subscription.id === preselectedPlanValue)
                        : false;
                      cards.push(
                        <div
                          className={`cf-subscription-radio-card${isChecked ? ' cf-subscription-selected' : ''}`}
                          data-plan-index={settingsIndex}
                          key={`subscription-card-${product.id}-${subscription.id}`}
                          onClick={handleCardSelect}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleCardSelect(event);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                        >
                          {showBadge && <div className="cf-subscription-popular-badge">{badgeText}</div>}
                          <div className="cf-subscription-radio-card-inner">
                            <div className="cf-subscription-radio-row" style={{ margin: 0, display: "flex", alignItems: "flex-start", gap: "10px", paddingTop: "2px" }}>
                              <input type="radio" id={subscription.id} name="cf-subscription-type" value={subscription.id} defaultChecked={isChecked} />
                              <div className="cf-subscription-plan-details" style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                                <label htmlFor={subscription.id}>{getSubscriptionLabel(subscriptionGroup, subscription)}</label>
                                {description && <div className="cf-subscription-radio-description" dangerouslySetInnerHTML={{ __html: description.replace(/text-align:\s*([^;"]+)/g, 'text-align: $1 !important') }} />}
                              </div>
                              {renderPlanPriceColumn(settingsIndex, subscription.price, subscription.priceCurrency, subscription.compareAtPrice)}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    planCounter++;
                  });
                });

                return cards;
              })()}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

const ElementProductHeroButton = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { element } = props;

  let buttonClasses = "element-content cf-button cf-button-primary cf-text-element";

  buttonClasses += " " + (element.options["button-size"] ? element.options["button-size"] : "cf-button-small");

  let buttonLayoutClasses =
    " " +
    (element.options["products-layout"] !== "list" && element.options["button-alignment"]
      ? element.options["button-alignment"]
      : "");

  let buttonText = element.html
    ? element.html
    : element.options["confirmation-type"] == "cart" && element.options["products-source"]?.toLowerCase() !== "convertflow"
    ? "<p>Add To Cart</p>"
    : "<p>Go To Product</p>";

  return (
    <React.Fragment>
      {element.options["products-button"] !== "false" || element.options["confirmation-type"] == "cart" ?
        <div className="cf-product-hero-outer-button">
          <div
            key={`product-hero-button-${element.id}`}
            className={`cf-row cf-outer-center cf-products-item-button-outer ${buttonLayoutClasses}`}
            data-element-id={element.id}
            data-font={element.options["button-font"]}
          >
            <div
              className={`${buttonClasses} cf-products-item-button cf-merge-tag-allowed`}
              dangerouslySetInnerHTML={{
                __html:
                  props.builder == true
                    ? "<div class='froala-editor'>" + buttonText + "</div>"
                    : buttonText,
              }}
            ></div>
          </div>
        </div>
      : null}
    </React.Fragment>
  )
}

const ElementProductHeroEmptyMessage = (props) => {
  if (props.condition == false) {
    return null;
  }

  const { element } = props;

  const emptyMessage = element.options["products-empty-message"]
    ? element.options["products-empty-message"]
    : "<p>Sorry, there are no products to display</p>";

  return (
    <React.Fragment>
      <div
        key={`product-hero-empty-message-${element.id}`}
        className="cf-row cf-outer-center cf-products-empty-message cf-merge-tag-allowed"
        dangerouslySetInnerHTML={{ __html: emptyMessage }}
        data-element-id={element.id}
      />
    </React.Fragment>
  )
}

const ElementProductHero = (props) => {
  const website = props.website;
  const element = props.element;

  const classes = style.classes();
  const stylesheet = buildProductHeroStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant
  );

  const placeholderProduct = {
    source: 'Convertflow',
    id: 1,
    title: "Placeholder 1",
    price: 1,
    description: "Example Description for Product 1",
    variants: [
      {
        id: 1,
        title: "Placeholder 1",
      },
    ],
    options: [
      {
        name: "1",
        values: ["1", "2", "3"],
      },
    ],
  }

  const [product, setProduct] = useState(placeholderProduct);

  const setupProductsFeed = () => {
    const integration = Object.values(website?.integrations).filter(
      (i) => i.integration_type == "Shopify" && i.status == true
    )[0]

    if (element.options["products-source"] == "ShopifyGraphql" && integration) {
      const parsed_store_name = (integration.api_data?.api_url || integration.api_data?.website)
        ?.replace("http://", "")
        ?.replace("https://", "")
        ?.replace(".myshopify.com", "")
        ?.replace("/", "")
        ?.replace(" ", "");

      const shopifyClient = new ShopifyGraphqlClient({
        store_name: parsed_store_name,
        token: integration.api_data["storefront-token"],
      });

      let productHandle;

      if (element.options["shopify-products-source"] == "filter" && element.options["shopify-products-current-fallback"]) {
        productHandle = element.options["shopify-products-current-fallback"];
      } else if (element.options["shopify-products-source"] == "selected" && element.options["shopify-products-selected"]) {
        productHandle = element.options["shopify-products-selected"];
      } else if (element.options["shopify-products-source"] == "current" && element.options["shopify-products-current-fallback"]) {
        productHandle = element.options["shopify-products-current-fallback"];
      } else if (element.options["shopify-products-source"] == "filter_quiz_answers" && element.options["shopify-products-current-fallback"]) {
        productHandle = element.options["shopify-products-current-fallback"];
      }

      if (productHandle) {
        // Check if product should be excluded (skip if source is "selected" or "current")
        if (element.options["shopify-products-excluded"] && ["selected", "current"].includes(element.options["shopify-products-source"]) == false) {
          const excludedHandles = element.options["shopify-products-excluded"].split(",").map(item => item.trim()).filter(item => item);
          if (excludedHandles.includes(productHandle)) {
            setProduct(placeholderProduct);
            return;
          }
        }

        new ProductsAdapterService(shopifyClient, "ShopifyGraphql", true)
          .getProduct(productHandle)
          .then((response) => {
            // Cache product-level subscriptions so the settings panel can access all plans
            window.cfBuilderProductSubscriptions = window.cfBuilderProductSubscriptions || {};
            window.cfBuilderProductSubscriptions[element.id] = {
              subscriptions: response.subscriptions,
              requires_selling_plan: response.requires_selling_plan
            };

            const selectedVariant = response.variants.find(
              (variant) =>
                variant.id ==
                element.options["shopify-products-selected-variants"]
            );

            if (selectedVariant) {
              setProduct(selectedVariant);
            } else {
              setProduct(response);
            }
          })
          .catch(() => {
            setProduct(placeholderProduct);
          })
      }
    } else {
      let product = Object.values(element.products)
        .find((product) => product.element_id == element.id && product.toBeDeleted !== true);

      const adapterService = new ProductsAdapterService(undefined, "Convertflow");

      if (product) {
        setProduct(adapterService.adaptProduct(product))
      } else {
        setProduct(placeholderProduct);
      }
    }
  }

  if (props.builder == true) {
    useEffect(() => {
      setupProductsFeed();
    }, [
      element.options["shopify-products-selected"],
      element.options["shopify-products-excluded"],
      element.options["products-source"],
      element.products
    ]);
  }

  let productTitleText;
  let selectedProductVariant = product?.variants && product?.variants[0];

  if (product?.source?.toLowerCase() == "convertflow" && product?.title && selectedProductVariant) {
    productTitleText = `${selectedProductVariant?.title?.includes('Default Title') ? product?.title : selectedProductVariant.title}`
  } else if (product?.title && selectedProductVariant && element.options["products-title-show-variant-name"] !== "false") {
    productTitleText = `${product?.title} ${selectedProductVariant.title.includes('Default Title') ? '' : ' - ' + selectedProductVariant.title}`
  } else if (product?.title) {
    productTitleText = product?.title;
  } else {
    productTitleText = `Product ${product?.id}`;
  }

  const ComponentTypes = {
    "offer-badge": ElementProductHeroOfferBadge,
    "reviews": ElementProductHeroReviews,
    "title": ElementProductHeroTitle,
    "price": ElementProductHeroPrice,
    "description": ElementProductHeroDescription,
    "quantity-picker": ElementProductHeroQuantityPicker,
    "variant-picker": ElementProductHeroVariantPicker,
    "subscription": ElementProductHeroSubscription,
    "button": ElementProductHeroButton,
  };

  const builtin_content_types = Object.keys(ComponentTypes)
    .filter((type) => isProductHeroBuiltinContentVisible(element, type))
    .map((type) => ({
      key: type,
      item_type: "builtin",
      show: element.options[`${type}-show`],
      position: parseInt(element.options[`${type}-position`]),
    }));

  const custom_content_items = Object.values(element.content_toggle_items || {})
    .filter((item) => item.element_id == element.id && item.toBeDeleted !== true)
    .filter((item) => (item.options && item.options["show"]) !== "false")
    .map((item) => ({
      key: `custom-${item.id}`,
      item_type: "custom",
      position: item.position,
      content_toggle_item_id: item.id,
      show: "true",
    }));

  const sorted_content_types = [...builtin_content_types, ...custom_content_items]
    .sort((a, b) => a.position - b.position)

  return (
    <React.Fragment>
      <div
        className={classes}
        data-product-id={product?.id}
        data-element-id={element.id}
      >
        <div className="cf-product-hero-inner cf-products-item" data-product-id={product.id}>
          <ElementProductHeroImage condition={element.options["image-show"] !== "false"} product={product} element={element} />

          <div className="cf-prod-hero-inner-box">
            <div className="cf-prod-hero-content-box">
              {sorted_content_types
                .map((content_type) => {
                  if (content_type.item_type === "custom") {
                    const toggle_item_section = Object.values(element.sections || {}).find(
                      (section) => section.content_toggle_item_id == content_type.content_toggle_item_id
                    );
                    if (toggle_item_section) {
                      return (
                        <div
                          className="cf-product-content-type cf-product-custom-element"
                          data-content-toggle-item-id={content_type.content_toggle_item_id}
                          key={content_type.key}
                        >
                          <Section
                            website={props.website}
                            cta={props.cta}
                            variant={props.variant}
                            builder={props.builder}
                            preview={props.preview}
                            step={props.step}
                            section={toggle_item_section}
                            device={props.device}
                          />
                        </div>
                      );
                    }
                    return null;
                  }
                  return (
                    <div className="cf-product-content-type" data-content-type={content_type.key} key={`cf-product-${element.id}-type-${content_type.key}`}>
                      {React.createElement(ComponentTypes[content_type.key], {
                        condition: content_type.show,
                        website: website,
                        product: product,
                        element: element,
                        productTitleText: productTitleText,
                      })}
                    </div>
                  )
                })}
            </div>
          </div>

          <BuilderStyleTag
            builder={props.builder}
            styleKey={`cta:${props.cta.id}:element:${element.id}:product_hero`}
            stylesheet={stylesheet}
          />

        </div>
      </div>

      <ElementProductHeroEmptyMessage element={element} />
    </React.Fragment>
  );
};

const style = {
  classes: () => {
    let classes = "cf-products-item--shopify-graphql cf-products-outer element-content cf-product-hero-element cf-inner-left";

    return classes;
  },
};

export default ElementProductHero;
